import React from "react";
import isEmpty from "lodash/isEmpty";
import trim from "lodash/trim";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { validateWhatJobsListing } from "@shared/lib/validateWithYup";
import SettingsContainer from "@ats/src/components/shared/SettingsContainer";
import UnsavedChangesGuard from "@ats/src/components/shared/UnsavedChangesGuard";
import Button from "@ats/src/components/shared/Button";
import LoadingIndicator from "@ats/src/components/shared/LoadingIndicator";
import FormSection from "@ats/src/components/forms/FormSection";
import FormSelect from "@ats/src/components/forms/FormSelect";
import FormTextarea from "@ats/src/components/forms/FormTextarea";
import FormInput from "@ats/src/components/forms/FormInput";
import FormLabel from "@ats/src/components/forms/FormLabel";
import FormConditionalFields from "@ats/src/components/forms/FormConditionalFields";
import Icon from "@ats/src/components/shared/Icon";
import WhatJobsPreviewModal from "@ats/src/components/modals/WhatJobsPreviewModal";
import WhatJobsSidebar from "@ats/src/views/jobApplications/jobDistribution/WhatJobsSidebar";

import { useModalContext } from "@shared/context/ModalContext";
import { useToastContext } from "@shared/context/ToastContext";

import {
  whatJobsEmploymentTypeOptions,
  whatJobsEmploymentSubTypeOptions,
  whatJobsScheduleTypeOptions,
  whatJobsStartTypeOptions,
  whatJobsSalaryDisplayTypeOptions,
  whatJobsContractPeriodOptions,
  deriveEmploymentType,
  deriveEmploymentSubType,
} from "@ats/src/lib/newLookups";
import { whatJobsSectorOptions } from "@ats/src/lib/lookups";
import { countWordsInPlainText } from "@ats/src/lib/utils/helpers";

import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import {
  useSaveBoardWhatJobsListingDraft,
  useUpdateActiveBoardWhatJobsListing,
} from "@shared/queryHooks/useWhatJobsListing";

function JobDistributionWhatJobs(props) {
  const { job, history, currentListing, isLoadingCurrentListing } = props;
  const { currentOrganization, currentOrganizationUser } = useCurrentSession();
  const addToast = useToastContext();
  const { openModal, removeModal } = useModalContext();

  window.logger("%c[JobDistributionWhatJobs] render", "color:rgb(15, 114, 40)", {
    currentListing,
    job,
    currentOrganization,
  });

  const { mutate: saveDraft, isLoading: isLoadingSaveDraft } = useSaveBoardWhatJobsListingDraft();
  const {
    mutate: updateActiveBoardWhatJobsListing,
    isLoading: isLoadingUpdateActiveBoardWhatJobsListing,
  } = useUpdateActiveBoardWhatJobsListing();

  const isLoading = isLoadingSaveDraft || isLoadingUpdateActiveBoardWhatJobsListing;

  // Extract state initialization logic
  const getInitialState = (job, currentListing, currentOrganization) => ({
    whatJobsCompanyDescription:
      currentListing?.whatJobsCompanyDescription ||
      currentOrganization?.whatjobsCompanyDescription ||
      currentOrganization?.wwrCompanyBio ||
      "",
    whatJobsEmploymentType:
      currentListing?.whatJobsEmploymentType || deriveEmploymentType(job?.kind),
    employmentSubType: currentListing?.employmentSubType || deriveEmploymentSubType(job?.kind),
    scheduleType: currentListing?.scheduleType || "",
    sector: currentListing?.sector || "",
    jobOpeningCount: currentListing?.jobOpeningCount || 1,
    startType: currentListing?.startType || "upon_acceptance",
    scheduleTypeOther: currentListing?.scheduleTypeOther || "",
    startByDate: currentListing?.startByDate || "",
    salaryDisplayType: currentListing?.salaryDisplayType || "not_disclosed",
    contractPeriod: currentListing?.contractPeriod || "months",
    contractLength: currentListing?.contractLength || "",
    streetAddress: currentListing?.streetAddress || "",
    zipCode: currentListing?.zipCode || "",
  });

  const [listing, setListing] = React.useState(() =>
    getInitialState(job, currentListing, currentOrganization),
  );

  React.useEffect(() => {
    if (currentListing) {
      setListing(getInitialState(job, currentListing, currentOrganization));
    }
  }, [currentListing, job, currentOrganization]);

  const {
    whatJobsCompanyDescription,
    whatJobsEmploymentType,
    employmentSubType,
    scheduleType,
    sector,
    jobOpeningCount,
    startType,
    scheduleTypeOther,
    startByDate,
    salaryDisplayType,
    contractPeriod,
    contractLength,
    streetAddress,
    zipCode,
  } = listing;

  const [errors, setErrors] = React.useState(null);
  const [isDirty, setIsDirty] = React.useState(false);
  const [isPurchasing, setIsPurchasing] = React.useState(false);

  // Keep only complex/reused derived values
  const expiresAt = currentListing?.whatJobsExpiresAt;
  const isExpired = currentListing?.expired;
  const isUnexpiredAndActiveOrPaused =
    (currentListing?.status === "active" || currentListing?.status === "paused") && !isExpired;
  const isDraftOrApproved =
    currentListing?.status === "draft" || currentListing?.status === "approved";

  // Check if payment has been received but listing not yet published
  const isPaidButProcessing =
    currentListing?.paid &&
    currentListing?.status === "draft" &&
    !currentListing?.whatJobsListingId;

  // Reset isPurchasing when listing moves to paid-but-processing or live state
  React.useEffect(() => {
    if (isPurchasing && (isPaidButProcessing || isUnexpiredAndActiveOrPaused)) {
      setIsPurchasing(false);
    }
  }, [isPurchasing, isPaidButProcessing, isUnexpiredAndActiveOrPaused]);

  // Determine if address is required
  // If listing doesn't exist yet or is draft/approved (editable), derive from current job.remoteness
  // If listing is active/paused/inactive (already published), use listing's saved whatJobsWorkFrom
  const requiresAddress =
    !currentListing || isDraftOrApproved
      ? job.remoteness !== "remoteness_only"
      : currentListing.whatJobsWorkFrom !== "remote";

  // Determine if location fields (city, state, country) are required
  // Location fields are only required for non-remote jobs
  const isNotFullyRemote = job.remoteness !== "remoteness_only";

  if (currentOrganization === undefined) {
    return null;
  }

  const ViewLink = (
    <Button type="externalLink" link="https://whatjobs.com" styleType="text">
      Visit WhatJobs
      <Icon name="external-link" />
    </Button>
  );

  // Check if job description meets word count requirements (150-1000 words)
  const jobDescriptionMeetsWordCount = (): boolean => {
    if (!job.descriptionWithoutHtml) return false;
    const wordCount = countWordsInPlainText(job.descriptionWithoutHtml);
    return wordCount >= 150 && wordCount <= 1000;
  };

  // Check if job title meets length requirements (3-120 characters)
  const jobTitleMeetsLengthRequirement = (): boolean => {
    if (!job.title) return false;
    const titleLength = job.title.trim().length;
    return titleLength >= 3 && titleLength <= 120;
  };

  const checkIfMissingRequirementsOutsideForm = () => {
    return [
      // Check base requirements (settings not in form)
      !currentOrganization?.stripeSubscriptionInGoodStanding ||
        !job.visible ||
        isEmpty(trim(job.description)),
      // Check job title length requirement (3-120 characters)
      !jobTitleMeetsLengthRequirement(),
      // Check location requirements (city, state, country - only required for non-remote jobs)
      !job.country || (isNotFullyRemote && (!job.city || !job.stateRegion)),
      // Check country is US
      job.country && job.country !== "US",
      // Check salary range requirement (from job settings, not form)
      salaryDisplayType === "disclose" && (!job.salaryMin || !job.salaryMax),
      // Check job description word count requirement (150-1000 words)
      !jobDescriptionMeetsWordCount(),
    ].some((condition) => condition);
  };

  // Check if requirements outside the form are not met
  const missingRequirementsOutsideForm = checkIfMissingRequirementsOutsideForm();

  const checkIfMissingFormRequirements = () => {
    return [
      // Check WhatJobs-specific requirements (only for purchase, not for draft save)
      !whatJobsCompanyDescription ||
        !whatJobsEmploymentType ||
        !employmentSubType ||
        !scheduleType ||
        !sector ||
        !jobOpeningCount ||
        !startType,
      // Check address fields when required (hybrid/onsite)
      requiresAddress && (!streetAddress || !zipCode),
      // Check conditional requirements
      scheduleType === "other" && !scheduleTypeOther,
      employmentSubType === "contract" && (!contractPeriod || !contractLength),
      startType === "specific_date" && !startByDate,
    ].some((condition) => condition);
  };

  // Purchase button should be disabled if job is not published or other requirements are missing
  const disablePurchaseButton =
    missingRequirementsOutsideForm ||
    job.status !== "published" ||
    checkIfMissingFormRequirements();

  // Update button should be disabled if requirements are missing
  // Draft save is always allowed (validation errors shown as warnings)
  const disableSaveUpdateButton = isUnexpiredAndActiveOrPaused
    ? disablePurchaseButton || isPurchasing
    : isPurchasing;

  const handleInputChange = (name, value) => {
    setListing({ ...listing, [name]: value });
    if (errors !== undefined) {
      setErrors(null);
    }
    setIsDirty(true);
  };

  const handleUpdateActiveListing = async () => {
    // Validate form - MUST be valid to update active listing
    const [isValid, validationErrors] = await validateWhatJobsListing(listing, requiresAddress);

    if (!isValid) {
      setErrors(validationErrors);
      addToast({
        title: "Please fix form errors before updating",
        kind: "warning",
      });
    } else {
      updateActiveBoardWhatJobsListing(
        { ...listing, jobId: job.id, id: currentListing.id },
        {
          onSuccess: () => {
            setIsDirty(false);
            setErrors(null);
            addToast({
              title: "Updating on WhatJobs",
              kind: "success",
            });
          },
          onError: (response: { data: any }) => {
            // Database errors come as plain arrays - show toast only, not form errors
            if (Array.isArray(response.data.errors)) {
              setErrors(null);
              addToast({
                title: response.data.errors[0] || "Failed to update listing",
                kind: "error",
              });
            } else {
              // Form validation errors come as objects - show in form AND toast
              setErrors(response.data.errors);
              addToast({
                title: response.data.errors?.general?.[0] || "Failed to update listing",
                kind: "error",
              });
            }
          },
        },
      );
    }
  };

  const handleSaveDraft = async () => {
    // Validate form but allow saving with errors (show them as warnings)
    const [isValid, validationErrors] = await validateWhatJobsListing(listing, requiresAddress);

    saveDraft(
      { ...listing, jobId: job.id },
      {
        onSuccess: () => {
          setIsDirty(false);
          // Show validation errors as warnings on successful save
          setErrors(!isValid && validationErrors ? validationErrors : null);
          addToast({
            title:
              "Draft saved successfully. Any form errors indicate required fields needed for purchase.",
            kind: "success",
          });
        },
        onError: (response: { data: any }) => {
          // Database errors come as plain arrays - show toast only, not form errors
          if (Array.isArray(response.data.errors)) {
            setErrors(null);
            addToast({
              title: response.data.errors[0] || "Failed to save draft",
              kind: "error",
            });
          } else {
            // Form validation errors come as objects - show in form AND toast
            setErrors(response.data.errors || null);
            addToast({
              title: response.data.errors?.general?.[0] || "Failed to save draft",
              kind: "error",
            });
          }
        },
      },
    );
  };

  const handleSaveChanges = () => {
    if (isUnexpiredAndActiveOrPaused) {
      handleUpdateActiveListing();
    } else {
      handleSaveDraft();
    }
  };

  const handleOpenModal = () => {
    const modal = (
      <WhatJobsPreviewModal
        job={job}
        formState={listing}
        whatJobsListing={currentListing}
        currentOrganization={currentOrganization}
        onCancel={removeModal}
        isUnexpiredAndActiveOrPaused={isUnexpiredAndActiveOrPaused}
      />
    );

    openModal(modal);
  };

  const Sidebar = (
    <WhatJobsSidebar
      isMissingAnyRequirements={disablePurchaseButton}
      errors={errors}
      jobId={job.id}
      currentListing={currentListing}
      listingData={{ ...listing, jobId: job.id }}
      hasPaymentMethod={currentOrganization?.stripeDefaultPaymentMethodOnFile}
      setIsPurchasing={setIsPurchasing}
      setIsDirty={setIsDirty}
      setErrors={setErrors}
      isExpired={isExpired}
      isLive={currentListing?.live} // unexpired & active (not paused)
      isUnexpiredAndActiveOrPaused={isUnexpiredAndActiveOrPaused}
      isPaidButProcessing={isPaidButProcessing}
      expiresAt={expiresAt}
      publishedAt={currentListing?.whatJobsPublishedAt}
      listingUrl={currentListing?.whatJobsJobUrl}
      isPurchasing={isPurchasing}
      isAdmin={currentOrganizationUser?.isAdmin}
    />
  );

  const BottomBar = (
    <>
      <UnsavedChangesGuard
        hasUnsavedChanges={isDirty}
        navigate={(pathname) => {
          setIsDirty(false);
          history.push(pathname);
        }}
      />

      <Styled.ButtonContainer>
        <Button
          className="submit-button"
          type="button"
          onClick={handleSaveChanges}
          loading={isLoading}
          disabled={disableSaveUpdateButton}
          aria-label={isUnexpiredAndActiveOrPaused ? "Update listing" : "Save draft"}
        >
          {isUnexpiredAndActiveOrPaused ? "Update listing" : "Save draft"}
        </Button>

        <Button
          className="submit-button"
          type="button"
          styleType="secondary"
          onClick={handleOpenModal}
        >
          {isUnexpiredAndActiveOrPaused ? "View listing info" : "Preview listing info"}
        </Button>
      </Styled.ButtonContainer>
    </>
  );

  const getSalaryRequirementMessage = () => {
    if (!job.salaryMin && !job.salaryMax) {
      return "Salary range is required when salary visibility is 'disclose'";
    } else if (!job.salaryMin) {
      return "Minimum salary is required when salary visibility is 'disclose'";
    } else if (!job.salaryMax) {
      return "Maximum salary is required when salary visibility is 'disclose'";
    }
    return "";
  };

  return (
    <>
      <Styled.SettingsContainerWrapper>
        <SettingsContainer
          fillHeight={true}
          title="WhatJobs?"
          description="WhatJobs is a leading job board connecting employers with qualified candidates across the United States. With their premium listing, your job is listed on WhatJobs and distributed across its network of 500+ recruitment partners and job boards, including ZipRecruiter, CareerBuilder, and many more."
          actions={ViewLink}
          sidebar={Sidebar}
          bottomBar={BottomBar}
        >
          {isLoadingCurrentListing ? (
            <LoadingIndicator label="Loading listing..." />
          ) : (
            <>
              {(missingRequirementsOutsideForm || job.status !== "published") && (
                <Styled.Requirements>
                  <FormLabel label="Job board requirements" />
                  <Styled.RequirementInstructions>
                    Your job post does not meet the following requirements for this job board.
                  </Styled.RequirementInstructions>
                  <div>
                    {job.country && job.country !== "US" ? (
                      <Styled.Requirement>
                        <Styled.RequirementTitle>
                          <Icon name="alert-circle" />
                          Currently the WhatJobs integration only supports the United States. More
                          countries being added soon!
                        </Styled.RequirementTitle>
                        <Button
                          type="internalLink"
                          link={`/jobs/${job.id}/setup/details`}
                          styleType="text"
                        >
                          Go to job details
                        </Button>
                      </Styled.Requirement>
                    ) : (
                      <>
                        {job.status === "status_archived" ? (
                          <Styled.Requirement>
                            <Styled.RequirementTitle>
                              <Icon name="alert-circle" />
                              Job has been archived. You may no longer post it as a listing on
                              WhatJobs.
                            </Styled.RequirementTitle>
                          </Styled.Requirement>
                        ) : job.status !== "published" ? (
                          <Styled.Requirement>
                            <Styled.RequirementTitle>
                              <Icon name="alert-circle" />
                              Job must be published
                            </Styled.RequirementTitle>
                            <Button
                              type="internalLink"
                              link={`/jobs/${job.id}/setup`}
                              styleType="text"
                            >
                              Go to job setup
                            </Button>
                          </Styled.Requirement>
                        ) : !job.visible ? (
                          <Styled.Requirement>
                            <Styled.RequirementTitle>
                              <Icon name="alert-circle" />
                              Job must be visible on your company job board (currently hidden).
                            </Styled.RequirementTitle>
                            <Button
                              type="internalLink"
                              link={`/jobs/${job.id}/setup`}
                              styleType="text"
                            >
                              Go to job setup
                            </Button>
                          </Styled.Requirement>
                        ) : null}
                        {!jobTitleMeetsLengthRequirement() && (
                          <Styled.Requirement>
                            <Styled.RequirementTitle>
                              <Icon name="alert-circle" />
                              Job title must be between 3 and 120 characters
                              {job.title &&
                                ` (currently ${job.title.trim().length} characters)`}.{" "}
                            </Styled.RequirementTitle>
                            <Button
                              type="internalLink"
                              link={`/jobs/${job.id}/setup/details`}
                              styleType="text"
                            >
                              Go to job details
                            </Button>
                          </Styled.Requirement>
                        )}
                        {isNotFullyRemote && !job.city && (
                          <Styled.Requirement>
                            <Styled.RequirementTitle>
                              <Icon name="alert-circle" />
                              Job must have a city specified
                            </Styled.RequirementTitle>
                            <Button
                              type="internalLink"
                              link={`/jobs/${job.id}/setup/details`}
                              styleType="text"
                            >
                              Go to job details
                            </Button>
                          </Styled.Requirement>
                        )}
                        {isNotFullyRemote && !job.stateRegion && (
                          <Styled.Requirement>
                            <Styled.RequirementTitle>
                              <Icon name="alert-circle" />
                              Job must have a state/region specified
                            </Styled.RequirementTitle>
                            <Button
                              type="internalLink"
                              link={`/jobs/${job.id}/setup/details`}
                              styleType="text"
                            >
                              Go to job details
                            </Button>
                          </Styled.Requirement>
                        )}
                        {!job.country && (
                          <Styled.Requirement>
                            <Styled.RequirementTitle>
                              <Icon name="alert-circle" />
                              Job must have a country specified
                            </Styled.RequirementTitle>
                            <Button
                              type="internalLink"
                              link={`/jobs/${job.id}/setup/details`}
                              styleType="text"
                            >
                              Go to job details
                            </Button>
                          </Styled.Requirement>
                        )}
                        {salaryDisplayType === "disclose" && (!job.salaryMin || !job.salaryMax) && (
                          <Styled.Requirement>
                            <Styled.RequirementTitle>
                              <Icon name="alert-circle" />
                              {getSalaryRequirementMessage()}
                            </Styled.RequirementTitle>
                            <Button
                              type="internalLink"
                              link={`/jobs/${job.id}/setup`}
                              styleType="text"
                            >
                              Go to job setup
                            </Button>
                          </Styled.Requirement>
                        )}
                        {!jobDescriptionMeetsWordCount() && (
                          <Styled.Requirement>
                            <Styled.RequirementTitle>
                              <Icon name="alert-circle" />
                              {`Job description must be between 150 and 1000 words (currently ${countWordsInPlainText(
                                job.descriptionWithoutHtml,
                              )} words)`}
                              .
                            </Styled.RequirementTitle>
                            <Button
                              type="internalLink"
                              link={`/jobs/${job.id}/setup/description`}
                              styleType="text"
                            >
                              Go to job description
                            </Button>
                          </Styled.Requirement>
                        )}
                      </>
                    )}
                  </div>
                </Styled.Requirements>
              )}

              <FormSection
                title="Job information"
                intro={
                  <p>
                    These fields help optimize how your job is listed on WhatJobs and distributed
                    across its 500+ partner job boards.
                  </p>
                }
              >
                <FormSelect
                  onChange={handleInputChange}
                  name="salaryDisplayType"
                  label="Salary visibility"
                  value={salaryDisplayType}
                  options={whatJobsSalaryDisplayTypeOptions}
                  errors={errors}
                  isDisabled={isPurchasing}
                />

                <FormSelect
                  onChange={handleInputChange}
                  name="sector"
                  label="Job sector"
                  isRequired={true}
                  value={sector}
                  options={whatJobsSectorOptions}
                  errors={errors}
                  isDisabled={isPurchasing}
                />

                {/* <FormInput
                  onChange={handleInputChange}
                  name="jobOpeningCount"
                  label="Job opening count"
                  type="number"
                  isRequired={true}
                  value={jobOpeningCount}
                  errors={errors}
                  min={1}
                  max={100}
                  isDisabled={isPurchasing}
                /> */}
              </FormSection>

              <FormSection
                title="Employment Details"
                intro={
                  <p>
                    WhatJobs requires detailed employment information to optimize their targeting.
                  </p>
                }
              >
                <FormSelect
                  onChange={handleInputChange}
                  name="whatJobsEmploymentType"
                  label="Employment type"
                  isRequired={true}
                  value={whatJobsEmploymentType}
                  options={whatJobsEmploymentTypeOptions}
                  errors={errors}
                  isDisabled={isPurchasing}
                />

                <FormSelect
                  onChange={handleInputChange}
                  name="employmentSubType"
                  label="Employment sub-type"
                  isRequired={true}
                  value={employmentSubType}
                  options={whatJobsEmploymentSubTypeOptions}
                  errors={errors}
                  isDisabled={isPurchasing}
                />

                {employmentSubType === "contract" && (
                  <FormConditionalFields>
                    <FormSelect
                      onChange={handleInputChange}
                      name="contractPeriod"
                      label="Contract period"
                      isRequired={true}
                      value={contractPeriod}
                      options={whatJobsContractPeriodOptions}
                      errors={errors}
                      isDisabled={isPurchasing}
                    />

                    <FormInput
                      onChange={handleInputChange}
                      name="contractLength"
                      label="Contract length"
                      type="number"
                      isRequired={true}
                      value={contractLength}
                      errors={errors}
                      min={1}
                      isDisabled={isPurchasing}
                    />
                  </FormConditionalFields>
                )}

                <FormSelect
                  onChange={handleInputChange}
                  name="scheduleType"
                  label="Schedule type"
                  isRequired={true}
                  value={scheduleType}
                  options={whatJobsScheduleTypeOptions}
                  errors={errors}
                  isDisabled={isPurchasing}
                />

                {scheduleType === "other" && (
                  <FormConditionalFields>
                    <FormTextarea
                      onChange={handleInputChange}
                      name="scheduleTypeOther"
                      label="Schedule description"
                      description="Describe the schedule requirements (30-300 words)."
                      value={scheduleTypeOther}
                      errors={errors}
                      isDisabled={isPurchasing}
                    />
                  </FormConditionalFields>
                )}

                <FormSelect
                  onChange={handleInputChange}
                  name="startType"
                  label="Start type"
                  isRequired={true}
                  value={startType}
                  options={whatJobsStartTypeOptions}
                  errors={errors}
                  isDisabled={isPurchasing}
                />

                {startType === "specific_date" && (
                  <FormConditionalFields>
                    <FormInput
                      onChange={handleInputChange}
                      name="startByDate"
                      label="Start by date"
                      isRequired={true}
                      value={startByDate}
                      errors={errors}
                      placeholder="YYYY-MM-DD"
                      description="Enter the date in YYYY-MM-DD format (e.g., 2025-12-31)"
                      isDisabled={isPurchasing}
                    />
                  </FormConditionalFields>
                )}
              </FormSection>

              {requiresAddress && (
                <FormSection
                  title="Location Details"
                  intro={
                    <p>
                      Street address and ZIP code are required for hybrid and onsite jobs. These
                      location fields cannot be changed after the job is live on WhatJobs.
                    </p>
                  }
                >
                  <FormInput
                    onChange={handleInputChange}
                    name="streetAddress"
                    label={requiresAddress ? "Street address" : "Street address (Optional)"}
                    description="Cannot be changed after the job is live on WhatJobs"
                    isRequired={requiresAddress}
                    value={streetAddress}
                    errors={errors}
                    maxLength={64}
                    isDisabled={isUnexpiredAndActiveOrPaused || isPurchasing}
                  />

                  <FormInput
                    onChange={handleInputChange}
                    name="zipCode"
                    label={requiresAddress ? "ZIP code" : "ZIP code (Optional)"}
                    description="Cannot be changed after the job is live on WhatJobs"
                    isRequired={requiresAddress}
                    value={zipCode}
                    errors={errors}
                    maxLength={5}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="12345"
                    isDisabled={isUnexpiredAndActiveOrPaused || isPurchasing}
                  />
                </FormSection>
              )}

              <FormSection title="Company">
                <FormTextarea
                  onChange={handleInputChange}
                  name="whatJobsCompanyDescription"
                  label="Company description"
                  description="Describe your company, culture, and mission."
                  value={whatJobsCompanyDescription}
                  errors={errors}
                  isRequired={true}
                  isDisabled={isPurchasing}
                />
              </FormSection>
            </>
          )}
        </SettingsContainer>
      </Styled.SettingsContainerWrapper>
    </>
  );
}

export default JobDistributionWhatJobs;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.ButtonContainer = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionWhatJobs_ButtonContainer;
    display: flex;
    align-items: center;

    > button:not(:last-child) {
      ${[t.mr(4)]}
    }
  `;
});

Styled.SettingsContainerWrapper = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionWhatJobs_SettingsContainerWrapper;
    > div {
      margin-bottom: 0;
    }
    > div > aside {
      padding-bottom: 100px;
    }
    > div > div:first-of-type {
      padding-bottom: 100px;
    }
  `;
});

Styled.Requirements = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionWhatJobs_Requirements;
    ${[t.mt(6), t.mb(8)]}
    display: flex;
    flex-direction: column;
  `;
});

Styled.RequirementInstructions = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionWhatJobs_RequirementInstructions;
    ${[t.text.xs, t.mt(-1), t.mb(2)]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    line-height: 1.4;
    max-width: 32rem;
  `;
});

Styled.Requirement = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionWhatJobs_Requirement;
    ${[t.px(4)]}
    min-height: ${t.spacing[12]};
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : "transparent"};
    border-top: 1px solid ${t.dark ? t.color.gray[700] : "transparent"};
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:first-of-type {
      border-top: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
      border-top-left-radius: 0.4375rem;
      border-top-right-radius: 0.4375rem;
    }

    &:last-of-type {
      border-bottom-left-radius: 0.4375rem;
      border-bottom-right-radius: 0.4375rem;
    }
  `;
});

Styled.RequirementTitle = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionWhatJobs_RequirementTitle;
    ${[t.my(2), t.mr(4)]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    display: flex;
    align-items: center;
    line-height: 1.4;

    svg {
      ${[t.mr(2)]};
      color: ${t.dark ? t.color.red[500] : t.color.red[600]};
      flex-shrink: 0;
    }
  `;
});
